<template>
  <validation-observer ref="footerLinkObserver">
    <b-form @submit.prevent="handleSubmit" class="form-container">
      <b-row>
        <b-col md="6">
          <h4 class="mb-3">Konfiguracja linku stopki</h4>

          <b-form-group class="mt-2" label="Link nadrzędny">
            <v-select
              id="v-parent-select"
              label="title"
              placeholder="Wybierz link nadrzędny"
              v-model="form.parent"
              :clearable="true"
              :deselectFromDropdown="false"
              :options="availableParents"
              :disabled="form.is_parent"
              @input="resetLanguage" />

            <small class="text-muted"> Wybierz link nadrzędny jeśli tworzysz tłumaczenie istniejącego linku </small>
          </b-form-group>

          <b-form-group class="mt-2" label="Język">
            <validation-provider #default="{ errors }" name="Język" rules="required">
              <v-select
                id="v-language-select"
                label="text"
                placeholder="Wybierz język"
                v-model="selectedLanguage"
                :clearable="false"
                :deselectFromDropdown="false"
                :options="availableLanguages"
                @input="onLanguageChange" />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <b-form-group class="mt-2" label="Sekcja stopki">
            <validation-provider #default="{ errors }" name="Sekcja stopki" rules="required">
              <v-select
                id="v-section-select"
                label="text"
                placeholder="Wybierz sekcję"
                v-model="selectedSection"
                :clearable="false"
                :deselectFromDropdown="false"
                :options="availableSections"
                :disabled="!selectedLanguage || !!form.parent"
                @input="onSectionChange" />
              <small class="text-danger">{{ errors[0] }}</small>
              <small v-if="form.parent" class="text-muted d-block"> Sekcja dziedziczona z linku nadrzędnego </small>
            </validation-provider>
          </b-form-group>

          <b-form-group class="mt-2" label="Typ linku">
            <validation-provider #default="{ errors }" name="Typ linku" rules="required">
              <v-select
                id="v-link-type-select"
                label="text"
                placeholder="Wybierz typ linku"
                v-model="selectedLinkType"
                :clearable="false"
                :deselectFromDropdown="false"
                :options="linkTypes"
                :disabled="!selectedLanguage || !!form.parent"
                @input="onLinkTypeChange" />
              <small class="text-danger">{{ errors[0] }}</small>
              <small v-if="form.parent" class="text-muted d-block"> Typ linku dziedziczony z linku nadrzędnego </small>
            </validation-provider>
          </b-form-group>

          <b-form-group v-if="form.link_type === 'internal'" class="mt-2" label="Ścieżka">
            <validation-provider
              #default="{ errors }"
              name="Nazwa trasy"
              :rules="form.link_type === 'internal' ? 'required' : ''">
              <b-form-input
                id="v-route-name"
                placeholder="np. outlet, about-us, /bezwzgledna-sciezka"
                v-model="form.route_name"
                :disabled="!selectedLanguage" />
              <small class="text-danger">{{ errors[0] }}</small>
              <small class="text-muted d-block">
                <span v-if="form.parent && form.parent.route_name">
                  Dziedziczone z linku nadrzędnego: <strong>{{ form.parent.route_name }}</strong>
                </span>
                <span v-else>
                  <strong>Możesz używać:</strong><br />
                  • <strong>Nazwy tras według nazw własnych</strong> (np. <em>promotions</em> → /promocje w PL,
                  /sale w EN; <em>outlet</em> → /outlet)<br />
                  • <strong>Ścieżki bezwzględne</strong> rozpoczynające się od "/" (np. <em>/produkty</em>,
                  <em>/o-nas</em>)<br />
                </span>
              </small>
            </validation-provider>
          </b-form-group>

          <b-form-group v-if="form.link_type === 'external'" class="mt-2" label="URL zewnętrzny">
            <validation-provider
              #default="{ errors }"
              name="URL zewnętrzny"
              :rules="form.link_type === 'external' ? 'required|url' : ''">
              <b-form-input
                id="v-external-url"
                placeholder="https://example.com"
                v-model="form.external_url"
                :disabled="!selectedLanguage" />
              <small class="text-danger">{{ errors[0] }}</small>
              <small class="text-muted d-block">
                <span v-if="form.parent && form.parent.external_url">
                  Dziedziczone z linku nadrzędnego: <strong>{{ form.parent.external_url }}</strong>
                </span>
                <span v-else> Podaj pełny adres URL zewnętrznej strony </span>
              </small>
            </validation-provider>
          </b-form-group>

          <b-form-group class="mt-2" label="Zachowanie linku">
            <v-select
              id="v-target-select"
              label="text"
              placeholder="Wybierz zachowanie"
              v-model="selectedTarget"
              :clearable="false"
              :deselectFromDropdown="false"
              :options="targetOptions"
              :disabled="!selectedLanguage || !!form.parent"
              @input="onTargetChange" />
            <small v-if="form.parent" class="text-muted"> Zachowanie dziedziczone z linku nadrzędnego </small>
          </b-form-group>
        </b-col>

        <b-col md="6">
          <h4 class="mb-3">Dane linku</h4>

          <b-form-group class="mt-2" label="Tytuł">
            <validation-provider #default="{ errors }" name="Tytuł" rules="required|min:2|max:100">
              <b-form-input id="v-title" placeholder="Tytuł linku" v-model="form.title" :disabled="!selectedLanguage" />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <b-form-group class="mt-2" label="Kolejność sortowania">
            <validation-provider #default="{ errors }" name="Kolejność sortowania" rules="required|numeric|min_value:0">
              <b-form-input
                id="v-sort-order"
                type="number"
                placeholder="0"
                v-model="form.sort_order"
                :disabled="!selectedLanguage" />
              <small class="text-danger">{{ errors[0] }}</small>
              <small class="text-muted d-block"> Niższa wartość = wyższa pozycja w menu </small>
            </validation-provider>
          </b-form-group>

          <b-form-group class="mt-2" label="Status">
            <b-form-checkbox v-model="form.is_active" :disabled="!selectedLanguage">
              Link aktywny (będzie wyświetlany w stopce)
            </b-form-checkbox>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row class="mt-4">
        <b-col>
          <div class="d-flex justify-content-end">
            <b-button
              variant="outline-secondary"
              class="mr-2"
              @click="$router.push({ name: 'footer-management-index' })">
              Anuluj
            </b-button>
            <b-button type="submit" variant="primary" :disabled="!selectedLanguage">
              {{ isEditing ? 'Zaktualizuj' : 'Zapisz' }}
            </b-button>
          </div>
        </b-col>
      </b-row>
    </b-form>
  </validation-observer>
</template>

<script>
import { getLanguages } from '@/constants/languages';
import { FOOTER_SECTIONS_OPTIONS } from '@/constants/enums/footer-sections';

export default {
  props: {
    footerLinks: {
      type: Array,
      default: () => [],
    },
    footerLink: {
      type: Object,
      default: null,
    },
    isEditing: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      form: {
        parent_id: null,
        lang: '',
        section_identifier: '',
        title: '',
        link_type: 'internal',
        route_name: '',
        external_url: '',
        target: '_self',
        is_active: true,
        sort_order: 0,
        parent: null,
        is_parent: false,
      },

      selectedLanguage: null,
      selectedSection: null,
      selectedLinkType: null,
      selectedTarget: null,

      availableLanguages: getLanguages().map((lang) => ({
        text: lang.value,
        value: lang.lang,
      })),

      availableSections: FOOTER_SECTIONS_OPTIONS,

      linkTypes: [
        { text: 'Link wewnętrzny', value: 'internal' },
        { text: 'Link zewnętrzny', value: 'external' },
      ],

      targetOptions: [
        { text: 'To samo okno', value: '_self' },
        { text: 'Nowa karta', value: '_blank' },
      ],
    };
  },

  computed: {
    availableParents() {
      return this.footerLinks.filter(
        (link) => link.parent_id === null && (!this.isEditing || link.id !== this.footerLink?.id),
      );
    },
  },

  watch: {
    footerLink: {
      handler(newVal) {
        if (newVal && this.isEditing) {
          this.populateForm();
        }
      },
      immediate: true,
    },

    'form.parent': {
      handler(newParent) {
        if (newParent && !this.isEditing) {
          this.inheritParentLinkData();
        }
      },
    },
  },

  methods: {
    populateForm() {
      if (!this.footerLink) return;

      this.form = {
        parent_id: this.footerLink.parent_id,
        lang: this.footerLink.lang,
        section_identifier: this.footerLink.section_identifier,
        title: this.footerLink.title,
        link_type: this.footerLink.link_type,
        route_name: this.footerLink.route_name || '',
        external_url: this.footerLink.external_url || '',
        target: this.footerLink.target,
        is_active: this.footerLink.is_active,
        sort_order: this.footerLink.sort_order,
        parent: this.footerLink.parent,
        is_parent: this.footerLink.parent_id === null,
      };

      // Set selected values for v-select components
      this.selectedLanguage = this.availableLanguages.find((lang) => lang.value === this.footerLink.lang);
      this.selectedSection = this.availableSections.find(
        (section) => section.value === this.footerLink.section_identifier,
      );
      this.selectedLinkType = this.linkTypes.find((type) => type.value === this.footerLink.link_type);
      this.selectedTarget = this.targetOptions.find((target) => target.value === this.footerLink.target);
    },

    resetLanguage() {
      this.selectedLanguage = null;
      this.form.lang = '';
      this.inheritParentLinkData();
    },

    inheritParentLinkData() {
      if (this.form.parent) {
        // Inherit section_identifier, link type, route_name, external_url, and target from parent
        this.form.section_identifier = this.form.parent.section_identifier;
        this.form.link_type = this.form.parent.link_type;
        this.form.route_name = this.form.parent.route_name || '';
        this.form.external_url = this.form.parent.external_url || '';
        this.form.target = this.form.parent.target || '_self';

        // Update the selected values to reflect inherited values
        this.selectedSection = this.availableSections.find((section) => section.value === this.form.section_identifier);
        this.selectedLinkType = this.linkTypes.find((type) => type.value === this.form.link_type);
        this.selectedTarget = this.targetOptions.find((target) => target.value === this.form.target);
      }
    },

    onLanguageChange(language) {
      this.form.lang = language ? language.value : '';
    },

    onSectionChange(section) {
      this.form.section_identifier = section ? section.value : '';
    },

    onLinkTypeChange(linkType) {
      this.form.link_type = linkType ? linkType.value : 'internal';

      // Clear the other URL field when switching types
      if (this.form.link_type === 'internal') {
        this.form.external_url = '';
      } else {
        this.form.route_name = '';
      }
    },

    onTargetChange(target) {
      this.form.target = target ? target.value : '_self';
    },

    async handleSubmit() {
      const isValid = await this.$refs.footerLinkObserver.validate();
      if (!isValid) return;

      // Prepare form data
      const formData = {
        parent_id: this.form.parent?.id || null,
        lang: this.form.lang,
        section_identifier: this.form.section_identifier,
        title: this.form.title,
        link_type: this.form.link_type,
        route_name: this.form.link_type === 'internal' ? this.form.route_name : null,
        external_url: this.form.link_type === 'external' ? this.form.external_url : null,
        target: this.form.target,
        is_active: this.form.is_active,
        sort_order: parseInt(this.form.sort_order),
      };

      this.$emit('onSubmit', formData);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@core/scss/vue/libs/vue-select.scss';

.form-container {
  max-width: 100%;
}

.border-top {
  border-top: 1px solid #dee2e6 !important;
}
</style>
